import React, { useState } from 'react';
import SEO from '../components/SEO';
import axios from 'axios';
import { Mail, Phone, MapPin } from 'lucide-react';
import { formatApiErrorDetail } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await axios.post(`${API_URL}/api/contacts`, formData);
      setSuccess(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      setError(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to submit contact form.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <SEO
        title="Contact Us - RESCO Invictus Green Energy Solutions"
        description="Get in touch with RESCO Invictus Green Energy Solutions for your C&I solar power plant requirements."
        keywords="contact, solar inquiry, get quote, solar consultation"
        url="/contact"
      />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-surface" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-accent mb-4 block" data-testid="contact-overline">
              Get In Touch
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-primary mb-6" data-testid="contact-title">
              Contact Us
            </h1>
            <p className="text-base leading-relaxed text-text_muted" data-testid="contact-description">
              Have a question or ready to start your solar journey? We'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div data-testid="contact-info">
              <h2 className="text-2xl sm:text-3xl font-semibold text-primary mb-6">Contact Information</h2>
              <p className="text-text_muted leading-relaxed mb-8">
                Reach out to us through any of the following channels. Our team is ready to assist you with your solar energy needs.
              </p>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-accent/10 rounded flex items-center justify-center flex-shrink-0">
                    <Mail className="text-accent" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-1">Email</h3>
                    <a href="mailto:invictuspowersolutions@gmail.com" className="text-text_muted hover:text-accent transition-colors block" data-testid="contact-email">
                      invictuspowersolutions@gmail.com
                    </a>
                    <a href="mailto:admin@invictusgreen.ooo" className="text-text_muted hover:text-accent transition-colors block" data-testid="contact-email-2">
                      admin@invictusgreen.ooo
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-accent/10 rounded flex items-center justify-center flex-shrink-0">
                    <Phone className="text-accent" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-1">Phone</h3>
                    <a href="tel:+918015600003" className="text-text_muted hover:text-accent transition-colors block" data-testid="contact-phone">
                      +91 8015600003
                    </a>
                    <a href="tel:+919872802140" className="text-text_muted hover:text-accent transition-colors block" data-testid="contact-phone-2">
                      +91 9872802140
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-accent/10 rounded flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-accent" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-1">Location</h3>
                    <p className="text-text_muted" data-testid="contact-address">
                      555-A Model Town Extension,<br />Ludhiana-141002, Punjab, India
                    </p>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-border">
                  <a href="https://www.invictusgreens.in" target="_blank" rel="noopener noreferrer" className="text-accent hover:text-primary font-medium transition-colors" data-testid="contact-website">
                    www.invictusgreens.in
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-surface p-8 rounded-sm border border-border" data-testid="contact-form">
              <h2 className="text-2xl font-semibold text-primary mb-6">Send Us a Message</h2>
              
              {success && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded text-green-700" data-testid="contact-success">
                  Thank you for contacting us! We'll get back to you soon.
                </div>
              )}

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded text-red-700" data-testid="contact-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-primary mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      data-testid="contact-input-name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-primary mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      data-testid="contact-input-email"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-primary mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      data-testid="contact-input-phone"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-primary mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      className="w-full px-4 py-3 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                      data-testid="contact-input-message"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full px-6 py-3 bg-accent text-white rounded font-semibold hover:bg-opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="contact-submit-button"
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
